// Phase 12 — Admin Activity Forms List + New Form Dialog
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Search, Trash2, Edit2, ChevronRight, CheckCircle2, PenSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";

const CATEGORIES = [
  { code: "general", label: "General" },
  { code: "btl", label: "BTL Activity" },
  { code: "display", label: "Display" },
  { code: "market_survey", label: "Market Survey" },
  { code: "distributor_admin", label: "Distributor Admin" },
  { code: "other", label: "Other" },
];

const DUP_POLICIES = [
  { code: "none", label: "No duplicate check" },
  { code: "once_per_day_per_user", label: "Once per day per user" },
  { code: "once_per_day_per_outlet_user", label: "Once per day per outlet per user" },
  { code: "once_per_visit", label: "Once per visit" },
];

const ROLES = ["SUPER_ADMIN", "MANAGEMENT", "SALES_HEAD", "ZSM", "RSM", "ASM", "SE", "DISTRIBUTOR", "MIS"];

const STATUS_STYLE = {
  draft: "bg-amber-100 text-amber-900 border-amber-200",
  published: "bg-emerald-100 text-emerald-900 border-emerald-200",
  inactive: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

export default function AdminFormsList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (q) params.q = q;
      if (status) params.status = status;
      if (category) params.category = category;
      if (role) params.role = role;
      const { data } = await api.get("/activity-forms", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [q, status, category, role]);

  async function onDelete(form) {
    if (!window.confirm(`Deactivate form '${form.name}'?`)) return;
    try {
      await api.delete(`/activity-forms/${form.id}`);
      toast.success("Form deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="admin-forms-list">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Activity forms" }]}
        title="Activity forms"
        subtitle="Dynamic forms field agents can submit at outlets."
        favorite={{ key: "admin.forms", type: "screen", label: "Activity forms", route: "/admin/forms" }}
        testid="adminformslist-page-header"
      />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Activity Forms</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Design no-code activity forms for field teams — BTL, display, market surveys, and more.
          </p>
        </div>
        <Button data-testid="new-form-btn" onClick={() => setOpenNew(true)}>
          <Plus className="w-4 h-4 mr-1" /> New Form
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="forms-search" placeholder="Search code / name…"
                 value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="forms-filter-status"><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="forms-filter-category"><SelectValue placeholder="Any category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any category</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={role || "__all__"} onValueChange={(v) => setRole(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="forms-filter-role"><SelectValue placeholder="Any role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any role</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Roles</th>
              <th className="px-4 py-3 font-medium">Fields</th>
              <th className="px-4 py-3 font-medium">Version</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-500">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-500">
                No forms yet. Click <b>New Form</b> to create one.
              </td></tr>
            )}
            {items.map((f) => (
              <tr key={f.id} className="border-t hover:bg-neutral-50" data-testid={`form-row-${f.code}`}>
                <td className="px-4 py-3 font-mono text-xs">{f.code}</td>
                <td className="px-4 py-3">
                  <Link to={`/admin/forms/${f.id}/builder`} className="text-emerald-700 hover:underline font-medium">
                    {f.name}
                  </Link>
                </td>
                <td className="px-4 py-3 capitalize">{(f.category || "—").replace(/_/g, " ")}</td>
                <td className="px-4 py-3">
                  {(f.applicable_role_codes || []).length === 0 ? (
                    <span className="text-neutral-400 italic text-xs">any</span>
                  ) : (f.applicable_role_codes.map(r => (
                    <Badge key={r} variant="outline" className="text-[10px] mr-1 mb-1">{r}</Badge>
                  )))}
                </td>
                <td className="px-4 py-3">{f.field_count ?? 0}</td>
                <td className="px-4 py-3">v{f.version || 1}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded border ${STATUS_STYLE[f.status] || ""}`}>
                    {f.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" data-testid={`open-builder-${f.code}`}
                          onClick={() => nav(`/admin/forms/${f.id}/builder`)}>
                    <PenSquare className="w-4 h-4 mr-1" /> Builder
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600"
                          data-testid={`delete-form-${f.code}`}
                          onClick={() => onDelete(f)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewFormDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
    </div>
  );
}


function NewFormDialog({ open, onOpenChange, onCreated }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [duplicatePolicy, setDuplicatePolicy] = useState("none");
  const [applicableRoles, setApplicableRoles] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleRole = (r) => setApplicableRoles((prev) =>
    prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  async function submit() {
    if (!code || !name) {
      toast.error("Code and name are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/activity-forms", {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description: description || null,
        category, duplicate_policy: duplicatePolicy,
        applicable_role_codes: applicableRoles,
        applicable_zone_ids: [], applicable_beat_ids: [],
        valid_from: "2020-01-01T00:00:00+00:00",
        valid_to: "2099-12-31T00:00:00+00:00",
      });
      toast.success("Form created (draft)");
      setCode(""); setName(""); setDescription("");
      setApplicableRoles([]);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-form-dialog">
        <DialogHeader>
          <DialogTitle>New Activity Form</DialogTitle>
          <DialogDescription>
            Create a draft form shell. Add fields in the builder, then publish when ready.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input data-testid="new-form-code" value={code}
                     onChange={(e) => setCode(e.target.value)}
                     placeholder="OUTLET-SURVEY" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="new-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Name</Label>
            <Input data-testid="new-form-name" value={name}
                   onChange={(e) => setName(e.target.value)}
                   placeholder="Outlet Survey" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="new-form-description" value={description}
                      onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div>
            <Label>Duplicate Policy</Label>
            <Select value={duplicatePolicy} onValueChange={setDuplicatePolicy}>
              <SelectTrigger data-testid="new-form-dup-policy"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DUP_POLICIES.map(p => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Applicable Roles <span className="text-neutral-400 text-xs">(empty = all)</span></Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {ROLES.map((r) => (
                <button key={r} type="button"
                        data-testid={`role-chip-${r}`}
                        onClick={() => toggleRole(r)}
                        className={`text-xs px-3 py-1 rounded-full border transition ${
                          applicableRoles.includes(r)
                            ? "bg-emerald-600 text-white border-emerald-700"
                            : "bg-white text-neutral-600 border-neutral-300 hover:border-emerald-500"
                        }`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-form-submit" onClick={submit} disabled={busy}>
            {busy ? "Creating…" : "Create Form"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
